/**
 * Helpers for the post-log recap (POST /reports with `recap: true`): what a
 * log accomplished, as before -> after. See api/services/recapService.js.
 */

const plural = (count, word) => `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;

function mergeCompletion(parts) {
  const present = parts.filter(Boolean);
  if (present.length === 0) return null;
  const last = present[present.length - 1];
  return {
    ...last,
    chaptersCloser: present.reduce((sum, p) => sum + (p.chaptersCloser || 0), 0),
    completed: present.some(p => p.completed)
  };
}

/**
 * One recap for a log that took several requests (logging several books):
 * "before" from the first, "after" from the last, community progress summed.
 */
export function mergeRecaps(recaps) {
  const list = (recaps || []).filter(Boolean);
  if (list.length <= 1) return list[0] || null;

  const first = list[0];
  const last = list[list.length - 1];
  return {
    reader: last.reader,
    chapters: first.chapters && last.chapters
      ? { before: first.chapters.before, after: last.chapters.after }
      : last.chapters,
    rank: first.rank && last.rank
      ? { before: first.rank.before, ofBefore: first.rank.ofBefore, after: last.rank.after, of: last.rank.of }
      : last.rank,
    church: mergeCompletion(list.map(r => r.church)),
    community: mergeCompletion(list.map(r => r.community))
  };
}

function completionLine(group, name, key) {
  if (!group) return null;
  if (group.completed) {
    return {
      key,
      highlight: true,
      text: `${name} just finished reading the whole Bible together. That's read-through #${group.completions}!`
    };
  }
  if (group.chaptersCloser > 0) {
    return {
      key,
      text: `Puts ${name} ${plural(group.chaptersCloser, 'chapter')} closer to reading the whole Bible together. ${group.chaptersToGo.toLocaleString()} to go.`
    };
  }
  return null;
}

/**
 * What the recap shows, in order. `stats` are before -> after rows; `lines`
 * are sentences about the church and the community. Anything that didn't
 * move is left out.
 */
export function describeRecap(recap) {
  if (!recap) return { stats: [], lines: [] };
  const stats = [];
  const who = recap.reader?.name;

  const chapters = recap.chapters;
  if (chapters && chapters.after > chapters.before) {
    stats.push({
      key: 'chapters',
      label: who ? `${who}'s chapters this season` : 'Your chapters this season',
      from: chapters.before.toLocaleString(),
      to: chapters.after.toLocaleString(),
      change: `+${(chapters.after - chapters.before).toLocaleString()}`
    });
  }

  const rank = recap.rank;
  if (rank?.after) {
    const label = who ? `${who}'s leaderboard rank` : 'Your leaderboard rank';
    const of = `of ${rank.of.toLocaleString()}`;
    if (rank.before == null) {
      stats.push({ key: 'rank', label, from: null, to: `#${rank.after}`, of, change: 'On the board' });
    } else if (rank.after < rank.before) {
      stats.push({
        key: 'rank',
        label,
        from: `#${rank.before}`,
        to: `#${rank.after}`,
        of,
        change: `Up ${rank.before - rank.after}`
      });
    } else if (stats.length > 0) {
      // Still worth showing where they stand alongside the chapter count.
      stats.push({ key: 'rank', label, from: null, to: `#${rank.after}`, of, change: null });
    }
  }

  const lines = [
    completionLine(recap.church, recap.church?.name, 'church'),
    completionLine(recap.community, 'the community', 'community')
  ].filter(Boolean);

  return { stats, lines };
}
